import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { weekKey } from "@shared/workweek";
import { SettingsDialog } from "@/components/SettingsDialog";
import { IntegrationsDialog } from "@/components/IntegrationsDialog";
import { SetTeamPrioritiesDialog } from "@/components/SetTeamPrioritiesDialog";
import { FirstRunGate } from "@/components/FirstRunGate";

/**
 * Global host for cross-page dialogs.
 *
 * Why: Settings, Integrations, and Set-Team-Priorities used to live
 * only on Canvas because their open state + dialog markup were
 * page-local. That meant every other page either had to duplicate
 * the dialog or live without the action. Lifting them here lets
 * AppHeader (used by every page) trigger them uniformly.
 *
 * The provider owns:
 *   - open/close state for each dialog
 *   - the team id needed for the priorities dialog (fetched once)
 *   - the "current week" key used as the dialog's default
 *
 * Page-specific overrides (e.g. Canvas wants the priorities dialog
 * keyed to whatever week the user is viewing) can pass `weekOverride`
 * to openPriorities — the provider falls back to "current week"
 * otherwise.
 */
type Ctx = {
  isManager: boolean;
  hasTeam: boolean;
  openSettings: () => void;
  openIntegrations: () => void;
  /** Pass a week key to override the default ("now") week the
   *  priorities dialog opens to. */
  openPriorities: (weekOverride?: string) => void;
};

const noop = () => {};
const GlobalDialogsContext = createContext<Ctx>({
  isManager: false,
  hasTeam: false,
  openSettings: noop,
  openIntegrations: noop,
  openPriorities: noop,
});

export function useGlobalDialogs() {
  return useContext(GlobalDialogsContext);
}

export function GlobalDialogsProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [integrationsOpen, setIntegrationsOpen] = useState(false);
  const [prioritiesOpen, setPrioritiesOpen] = useState(false);
  const [prioritiesWeekKey, setPrioritiesWeekKey] = useState<string | null>(
    null,
  );

  // Resolve the caller's primary team — needed by SetTeamPriorities.
  // We pull team.mine here so every header consumer doesn't have to.
  const teamMineQ = trpc.team.mine.useQuery(undefined, {
    enabled: isAuthenticated,
    staleTime: 60_000,
  });
  // team.mine returns `[{ team: { id, name, ... }, role }]` — the
  // team is nested, NOT flat. Reading `myEntry.id` was producing
  // undefined → hasTeam=false → the Team Priorities icon was hidden
  // for everyone, including managers.
  const myEntry = teamMineQ.data?.[0] as
    | { team?: { id?: number; name?: string }; role?: string }
    | undefined;
  const myTeamId = myEntry?.team?.id;
  const isManager =
    myEntry?.role === "owner" || myEntry?.role === "manager";
  const hasTeam = !!myTeamId;

  const openSettings = useCallback(() => setSettingsOpen(true), []);
  const openIntegrations = useCallback(() => setIntegrationsOpen(true), []);
  const openPriorities = useCallback((weekOverride?: string) => {
    setPrioritiesWeekKey(weekOverride ?? weekKey(new Date(), 0));
    setPrioritiesOpen(true);
  }, []);

  const ctx = useMemo(
    () => ({
      isManager,
      hasTeam,
      openSettings,
      openIntegrations,
      openPriorities,
    }),
    [isManager, hasTeam, openSettings, openIntegrations, openPriorities],
  );

  return (
    <GlobalDialogsContext.Provider value={ctx}>
      {children}
      {/* First-run / compliance modals — hosted here so they fire on every
          route, including the post-cutover default path (`/` = Home). */}
      <FirstRunGate />
      <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
      <IntegrationsDialog
        open={integrationsOpen}
        onOpenChange={setIntegrationsOpen}
      />
      {/* Mounted lazily — only render once the manager has actually
          opened it. Avoids paying the dialog's setup cost (member
          queries, signals queries) on every page load. */}
      {myTeamId && prioritiesWeekKey && (
        <SetTeamPrioritiesDialog
          open={prioritiesOpen}
          onOpenChange={setPrioritiesOpen}
          teamId={myTeamId}
          weekStartKey={prioritiesWeekKey}
        />
      )}
    </GlobalDialogsContext.Provider>
  );
}
